import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';

import {
  hasRoles,
  DATA_CAPTURERS,
  DATA_MANAGERS,
  INVESTIGATORS,
  PROJECT_MANAGERS,
  STUDY_COORDINATORS,
} from '../../../util/RoleUtil';

import { retrieveDropdownsForAddStudyToSite } from '../../../services/FacilitiesService';
import { retrieveStudyDropdownsSpecific, updateStudyAddSite } from '../../../services/StudyService';

import { SaveButton } from '../../common/button/save-button/SaveButton';

import './AddSiteToStudy.css';

export const AddSiteToStudy = ({ context, showToaster, setSpinnerText, setIsModalOpen, navigateToWith }) => {
  const [selectedProtocolID, setSelectedProtocolID] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSubDistrict, setSelectedSubDistrict] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);

  const [studyExternalReference, setStudyExternalReference] = useState(null);
  const [siteActivationDate, setSiteActivationDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [accrualPeriodFrom, setAccrualPeriodFrom] = useState(null);
  const [accrualPeriodTo, setAccrualPeriodTo] = useState(null);

  const [studies, setStudies] = useState([]);
  const [currentSites, setCurrentSites] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityExtRef, setFacilityExtRef] = useState('');

  const [protocolIDError, setProtocolIDError] = useState('');
  const [provinceError, setProvinceError] = useState('');
  const [districtError, setDistrictError] = useState('');
  const [subDistrictError, setSubDistrictError] = useState('');
  const [facilityError, setFacilityError] = useState('');
  const [siteActivationDateError, setSiteActivationDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [accrualPeriodFromError, setAccrualPeriodFromError] = useState('');
  const [accrualPeriodToError, setAccrualPeriodToError] = useState('');

  const editable = hasRoles(context, [
    STUDY_COORDINATORS,
    PROJECT_MANAGERS,
    DATA_MANAGERS,
    INVESTIGATORS,
    DATA_CAPTURERS,
  ]);

  const populateProtocolIDs = useCallback(async () => {
    setSpinnerText('Retrieving Protocol IDs');

    try {
      const [responseData, error] = await retrieveStudyDropdownsSpecific(context.username);

      if (responseData) {
        if (responseData.responseStatus === 'Success') {
          setStudies([...responseData.dropdownStudyVOs]);
        } else {
          showToaster(responseData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    } catch (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message || 'Error Retrieving Protocol IDs',
      });
    }

    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  useEffect(() => {
    populateProtocolIDs();
  }, [populateProtocolIDs]);

  const populateDropdowns = async (studyExternalReference) => {
    setSpinnerText('Retrieving Facilities');

    try {
      const [responseData, error] = await retrieveDropdownsForAddStudyToSite(context.username, studyExternalReference);

      if (responseData) {
        if (responseData.responseStatus === 'Success') {
          const formattedProvinces = responseData.provinces.map((province) => ({
            value: province,
            label: province.province,
          }));

          setProvinces([...formattedProvinces]);
          setCurrentSites(responseData.currentSites);
        } else {
          showToaster(responseData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    } catch (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message || 'Error Retrieving Facilities',
      });
    }

    setSpinnerText(null);
  };

  const validateRequiredFields = () => {
    let isValid = true;

    if (!selectedProtocolID) {
      setProtocolIDError('*REQUIRED');
      isValid = false;
    } else {
      setProtocolIDError('');
    }

    if (!selectedProvince) {
      setProvinceError('*REQUIRED');
      isValid = false;
    } else {
      setProvinceError('');
    }

    if (!selectedDistrict) {
      setDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setDistrictError('');
    }

    if (!selectedSubDistrict) {
      setSubDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setSubDistrictError('');
    }

    if (!selectedFacility) {
      setFacilityError('*REQUIRED');
      isValid = false;
    } else {
      setFacilityError('');
    }

    if (!siteActivationDate) {
      setSiteActivationDateError('*REQUIRED');
      isValid = false;
    } else {
      setSiteActivationDateError('');
    }

    if (!endDate) {
      setEndDateError('*REQUIRED');
      isValid = false;
    } else {
      setEndDateError('');
    }

    if (!accrualPeriodFrom) {
      setAccrualPeriodFromError('*REQUIRED');
      isValid = false;
    } else {
      setAccrualPeriodFromError('');
    }

    if (!accrualPeriodTo) {
      setAccrualPeriodToError('*REQUIRED');
      isValid = false;
    } else {
      setAccrualPeriodToError('');
    }

    if (siteActivationDate && endDate && endDate <= siteActivationDate) {
      setSiteActivationDateError('*End Date cannot be before Start Date');
      setEndDateError('*End Date cannot be before Start Date');
      isValid = false;
    }

    return isValid;
  };

  const handleProvinceChange = (selectedProvince) => {
    setSelectedProvince(selectedProvince);
    setProvinceError('');

    setSelectedDistrict(null);
    setSelectedSubDistrict(null);
    setSelectedFacility(null);
    setDistricts([]);
    setSubDistricts([]);
    setFacilities([]);
    setFacilityExtRef('');

    if (!selectedProvince) return;

    const selectedProvinceData = selectedProvince.value;
    const districtOptions = selectedProvinceData.districts.map((district) => ({
      value: district,
      label: district.district,
    }));

    setDistricts(districtOptions);
  };

  const handleDistrictChange = (selectedDistrict) => {
    setSelectedDistrict(selectedDistrict);
    setDistrictError('');

    setSelectedSubDistrict(null);
    setSelectedFacility(null);
    setSubDistricts([]);
    setFacilities([]);
    setFacilityExtRef('');

    if (!selectedDistrict) return;

    const selectedDistrictData = selectedDistrict.value;
    const subDistrictOptions = selectedDistrictData.subDistricts.map((subDistrict) => ({
      value: subDistrict,
      label: subDistrict.subDistrict,
    }));

    setSubDistricts(subDistrictOptions);
  };

  const handleSubDistrictChange = (selectedSubDistrict) => {
    setSelectedSubDistrict(selectedSubDistrict);
    setSubDistrictError('');

    setSelectedFacility(null);
    setFacilities([]);
    setFacilityExtRef('');

    if (!selectedSubDistrict) return;

    const selectedSubDistrictData = selectedSubDistrict.value;
    const facilityOptions = selectedSubDistrictData.facilities.map((facility) => ({
      value: facility,
      label: facility.facility,
    }));

    setFacilities(facilityOptions);
  };

  const handleFacilityChange = (selectedFacility) => {
    setSelectedFacility(selectedFacility);
    setFacilityError('');

    if (!selectedFacility) {
      setFacilityExtRef('');
      return;
    }

    setFacilityExtRef(selectedFacility.value.externalReference);
  };

  const handleSaveAndNextClick = async () => {
    const isValid = validateRequiredFields();

    if (!isValid) {
      return;
    }

    const addStudyToSiteVo = {
      accrualPeriodFrom,
      accrualPeriodTo,
      endDate,
      facilityExternalReference: facilityExtRef,
      siteActivationDate,
      studyExternalReference,
    };

    setSpinnerText('Adding Site To Study');

    try {
      const [responseData, error] = await updateStudyAddSite(context.username, addStudyToSiteVo);

      if (responseData) {
        if (responseData.responseStatus === 'Success') {
          showToaster(responseData.responseMessage);

          const studyTrial = {
            facilityExternalReference: responseData.facilityExternalReference,
            studyExternalReference: responseData.studyExternalReference,
          };

          navigateToWith('studyTrialEdit', 'studyTrialEditGeneral', studyTrial);
        } else {
          showToaster(responseData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    } catch (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message || 'Error Adding Site To Study',
      });
    }

    setSpinnerText(null);
  };

  const handleProtocolIDChange = (selectedProtocolID) => {
    setSelectedProtocolID(selectedProtocolID);
    setProtocolIDError('');

    const selectedStudyExternalReference = selectedProtocolID?.studyExternalReference;

    setStudyExternalReference(selectedStudyExternalReference);

    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedSubDistrict(null);
    setSelectedFacility(null);
    setFacilityExtRef('');
    setProvinces([]);
    setDistricts([]);
    setSubDistricts([]);
    setFacilities([]);
    setCurrentSites([]);

    if (selectedStudyExternalReference) {
      populateDropdowns(selectedStudyExternalReference);
    }
  };

  return (
    <div className="add-site-to-study-container">
      <div className="outer-container">
        <div className="protocol-and-site-container">
          <div className="input-and-field">
            <label className="label">Protocol ID:</label>
            <Select
              options={studies}
              closeMenuOnSelect={true}
              onChange={handleProtocolIDChange}
              getOptionLabel={(study) => study.name}
              getOptionValue={(study) => study.name}
              value={selectedProtocolID}
              isDisabled={!editable}
              classNamePrefix={`input-select${protocolIDError ? 'error-border' : ''}`}
            />
          </div>
          <div className="add-site-error-message">
            {protocolIDError && <div className="small-red-text">{protocolIDError}</div>}
          </div>

          <h3 className="form-section-heading">Site:</h3>

          <div className="input-and-field">
            <label className="label">Province:</label>
            <Select
              options={provinces}
              closeMenuOnSelect={true}
              onChange={handleProvinceChange}
              value={selectedProvince}
              isDisabled={!editable}
              classNamePrefix={`input-select${provinceError ? 'error-border' : ''}`}
            />
          </div>
          <div className="add-site-error-message">
            {provinceError && <div className="small-red-text">{provinceError}</div>}
          </div>

          <div className="input-and-field">
            <label className="label">District:</label>
            <Select
              options={districts}
              closeMenuOnSelect={true}
              onChange={handleDistrictChange}
              value={selectedDistrict}
              isDisabled={!editable}
              classNamePrefix={`input-select${districtError ? 'error-border' : ''}`}
            />
          </div>
          <div className="add-site-error-message">
            {districtError && <div className="small-red-text">{districtError}</div>}
          </div>

          <div className="input-and-field">
            <label className="label">Sub-District:</label>
            <Select
              options={subDistricts}
              closeMenuOnSelect={true}
              onChange={handleSubDistrictChange}
              value={selectedSubDistrict}
              isDisabled={!editable}
              classNamePrefix={`input-select${subDistrictError ? 'error-border' : ''}`}
            />
          </div>
          <div className="add-site-error-message">
            {subDistrictError && <div className="small-red-text">{subDistrictError}</div>}
          </div>

          <div className="input-and-field">
            <label className="label">Facility:</label>
            <Select
              options={facilities}
              closeMenuOnSelect={true}
              onChange={handleFacilityChange}
              value={selectedFacility}
              isDisabled={!editable}
              classNamePrefix={`input-select${facilityError ? 'error-border' : ''}`}
            />
          </div>
          <div className="add-site-error-message">
            {facilityError && <div className="small-red-text">{facilityError}</div>}
          </div>

          <h3 className="form-section-heading">Dates:</h3>
        </div>

        <div className="protocol-sites-container">
          <label className="label">Current Sites:</label>
          <div className="site-list">
            {currentSites.map((site, index) => (
              <p key={index} className="input-label">
                {site}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="outer-container">
        <div className="date-container">
          <div className="input-and-field">
            <label className="label">Site Activation Date:</label>
            <DatePicker
              selected={siteActivationDate}
              className={`text-input ${siteActivationDateError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={!editable}
              onChange={(selectedDate) => {
                setSiteActivationDate(selectedDate);
                setSiteActivationDateError('');
                setEndDateError('');
              }}
            />
          </div>
          <div className="add-site-error-message">
            {siteActivationDateError && <div className="small-red-text">{siteActivationDateError}</div>}
          </div>

          <div className="input-and-field">
            <label className="label">Accrual Period From:</label>
            <DatePicker
              selected={accrualPeriodFrom}
              className={`text-input ${accrualPeriodFromError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={!editable}
              onChange={(selectedDate) => {
                setAccrualPeriodFrom(selectedDate);
                setAccrualPeriodFromError('');
              }}
            />
          </div>
          <div className="add-site-error-message">
            {accrualPeriodFromError && <div className="small-red-text">{accrualPeriodFromError}</div>}
          </div>
        </div>

        <div className="date-container">
          <div className="input-and-field">
            <label className="label">End Date:</label>
            <DatePicker
              selected={endDate}
              className={`text-input ${endDateError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={!editable}
              onChange={(selectedDate) => {
                setEndDate(selectedDate);
                setEndDateError('');
                setSiteActivationDateError('');
              }}
            />
          </div>
          <div className="add-site-error-message">
            {endDateError && <div className="small-red-text">{endDateError}</div>}
          </div>

          <div className="input-and-field">
            <label className="label">Accrual Period To:</label>
            <DatePicker
              selected={accrualPeriodTo}
              className={`text-input ${accrualPeriodToError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={!editable}
              onChange={(selectedDate) => {
                setAccrualPeriodTo(selectedDate);
                setAccrualPeriodToError('');
              }}
            />
          </div>
          <div className="add-site-error-message">
            {accrualPeriodToError && <div className="small-red-text">{accrualPeriodToError}</div>}
          </div>
        </div>
      </div>

      <div className="study-button-container">
        <SaveButton
          color="#ffffff"
          border="1px solid #db602e"
          textColor="#db602e"
          title="Cancel"
          onClick={() => setIsModalOpen(false)}
        />
        <SaveButton color="#db602e" title="Save and Next" onClick={handleSaveAndNextClick} />
      </div>
    </div>
  );
};
